import React, { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { Card } from '../types';

interface GameCardProps {
  card: Card;
  index: number;
  spymasterMode: boolean;
  onClick: (index: number) => void;
}

export const GameCard: React.FC<GameCardProps> = ({ card, index, spymasterMode, onClick }) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [clickCount, setClickCount] = useState(0);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const winner = useGameStore((state) => state.winner);
  const showFullBoard = useGameStore((state) => state.showFullBoard);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  const getCardClass = () => {
    let className = 'card';
    
    if (isAnimating) {
      className += ' animating';
    }
    
    // Show visual feedback for single click (ready for double click)
    if (clickCount === 1 && !card.revealed) {
      className += ' single-clicked';
    }
    
    // Show card type if revealed, in spymaster mode, or if game is over and full board is shown
    if (card.revealed) {
      className += ` revealed ${card.type}`;
    } else if (spymasterMode) {
      className += ` spymaster ${card.type}`;
    } else if (winner && showFullBoard) {
      className += ` revealed ${card.type}`;
    }
    
    return className;
  };

  const handleClick = () => {
    if (card.revealed || isAnimating) {
      return; // Don't handle clicks on already revealed cards or during animation
    }

    setClickCount(prev => {
      const newCount = prev + 1;
      
      if (newCount === 1) {
        // First click - start timeout for double click detection
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
        }
        
        clickTimeoutRef.current = setTimeout(() => {
          // Reset if no second click within timeout
          setClickCount(0);
        }, 500); // 500ms timeout for double click
        
        return 1;
      } else if (newCount === 2) {
        // Second click (double click) - reveal the card
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
          clickTimeoutRef.current = null;
        }
        
        setIsAnimating(true);
        // Reset animation after it completes
        setTimeout(() => setIsAnimating(false), 400);
        
        // Reset click count and trigger the card reveal
        setTimeout(() => {
          setClickCount(0);
          onClick(index);
        }, 0);
        
        return 0;
      }
      
      return newCount;
    });
  };

  return (
    <div className={getCardClass()} onClick={handleClick}>
      <div className="card-content">
        <span className="card-word">{card.word}</span>
        <span className="card-index">{index + 1}</span>
      </div>
      <div className="card-reveal-overlay"></div>
    </div>
  );
};
